/**
 * Badge de la plataforma: una sola pastilla para todos los estatus, etiquetas
 * y categorías, con los colores del módulo de almacén (la referencia).
 *
 * Quien lo usa decide el TEXTO y el COLOR; el tamaño, el redondeo, la
 * tipografía y los tonos de cada color los pone el componente, para que un
 * gris sea el mismo gris en cualquier pantalla.
 *
 * @example
 * <Badge color="verde">Aprobado</Badge>
 * <Badge color="amarillo" dot>Pendiente</Badge>
 * <Badge color="rojo" icon={<X />}>Rechazado</Badge>
 */

import { ChevronDown } from 'lucide-react';
import React from 'react';

export type ColorDeBadge =
  | 'verde'
  | 'azul'
  | 'rojo'
  | 'amarillo'
  | 'naranja'
  | 'morado'
  | 'gris';

/** Fondo y texto de cada color, tomados de los badges de almacén. */
const COLORES: Record<ColorDeBadge, string> = {
  verde: 'bg-[#EBFCF1] text-[#1AC358]',
  azul: 'bg-[#E0F2FE] text-[#0284C7]',
  rojo: 'bg-[#FBEEEE] text-[#F15C5C]',
  amarillo: 'bg-[#FEF9EC] text-[#D4A112]',
  naranja: 'bg-[#FFF3E8] text-[#EA7C2B]',
  morado: 'bg-[#F5F2FF] text-[#895CFF]',
  gris: 'bg-[#F3F4F6] text-[#6B7280]',
};

/**
 * Nombres anteriores del componente (success, warning…). Se conservan para no
 * romper los usos viejos; el código nuevo usa `color`.
 */
export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'primary';

const DE_VARIANTE: Record<BadgeVariant, ColorDeBadge> = {
  success: 'verde',
  warning: 'amarillo',
  error: 'rojo',
  danger: 'rojo',
  info: 'azul',
  neutral: 'gris',
  primary: 'morado',
};

interface BadgeProps {
  /** Texto a mostrar. */
  children: React.ReactNode;
  /** Color de la pastilla. */
  color?: ColorDeBadge;
  /** @deprecated Usa `color`. */
  variant?: BadgeVariant;
  /** @deprecated El tamaño lo decide el componente; se ignora. */
  size?: 'sm' | 'md' | 'lg';
  /** Punto de color antes del texto (sigue el color del texto). */
  dot?: boolean;
  /** Ícono opcional antes del texto. */
  icon?: React.ReactNode;
  /** Solo para layout (margen, ancho); nunca para color ni tamaño. */
  className?: string;
  onClick?: () => void;
  title?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  color,
  variant,
  dot = false,
  icon,
  className = '',
  onClick,
  title,
}) => {
  const colorFinal: ColorDeBadge = color ?? (variant ? DE_VARIANTE[variant] : 'gris');
  return (
    <span
      onClick={onClick}
      title={title}
      className={`inline-flex min-h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium ${COLORES[colorFinal]} ${className}`}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {icon && <span className="inline-flex">{icon}</span>}
      {children}
    </span>
  );
};

/**
 * Helpers para badges específicos del módulo de empleados
 */

// Badge para estado de documentos
export const DocumentStatusBadge: React.FC<{ status: string, onAction?: () => void }> = ({ status, onAction }) => {
  const statusConfig: Record<string, { color: ColorDeBadge; label: string }> = {
    pendiente: { color: 'amarillo', label: 'Pendiente' },
    en_revision: { color: 'azul', label: 'En Revisión' },
    aprobado: { color: 'verde', label: 'Aprobado' },
    rechazado: { color: 'rojo', label: 'Rechazado' },
    vencido: { color: 'rojo', label: 'Vencido' },
    no_aplica: { color: 'gris', label: 'No Aplica' },
    sin_cargar: { color: 'gris', label: 'Sin Cargar' },
  };

  const config = statusConfig[status] || { color: 'gris' as ColorDeBadge, label: status };

  return (
    <Badge color={config.color} onClick={() => {
      if(onAction) onAction();
    }} className={onAction ? 'cursor-pointer' : ''}>
      {config.label}
      {onAction && <ChevronDown className='w-4 h-4'/>}
    </Badge>
  );
};

// Badge para estado de contratos
export const ContractStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { color: ColorDeBadge; label: string }> = {
    borrador: { color: 'gris', label: 'Borrador' },
    generado: { color: 'azul', label: 'Generado' },
    enviado: { color: 'amarillo', label: 'Enviado' },
    firmado: { color: 'verde', label: 'Firmado' },
    activo: { color: 'verde', label: 'Activo' },
    terminado: { color: 'rojo', label: 'Terminado' },
    cancelado: { color: 'rojo', label: 'Cancelado' },
    en_revision: { color: 'azul', label: 'En revisión' },
  };

  const config = statusConfig[status] || { color: 'gris' as ColorDeBadge, label: status };

  return <Badge color={config.color}>{config.label}</Badge>;
};

// Badge para IMSS/ERP
export const EnabledBadge: React.FC<{ enabled: boolean; label?: string }> = ({
  enabled,
  label,
}) => {
  return (
    <Badge color={enabled ? 'verde' : 'gris'}>
      {label || (enabled ? 'Habilitado' : 'Deshabilitado')}
    </Badge>
  );
};
